import React, { useState, useContext, useEffect } from "react";
import { AuthContext } from "../context/authContext";
import { login } from "../services/authClient";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiEye,
  FiEyeOff,
  FiLoader,
  FiCheckCircle,
  FiAlertCircle,
  FiArrowLeft,
  FiLock,
  FiMail,
} from "react-icons/fi";

function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [focusedField, setFocusedField] = useState("");

  const { auth, refreshAuthState, applyToken } = useContext(AuthContext);
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (auth.isAuthenticated) {
      if (process.env.NODE_ENV === "production") {
        window.location.replace("/admin-dashboard");
      } else {
        navigate("/admin-dashboard", { replace: true });
      }
    }
  }, [auth.isAuthenticated, navigate]);

  // Validate email format
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle form validation
  const validateForm = () => {
    let isValid = true;
    setEmailError("");
    setPasswordError("");

    if (!email.trim()) {
      setEmailError("Email is required");
      isValid = false;
    } else if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      isValid = false;
    }

    if (!password.trim()) {
      setPasswordError("Password is required");
      isValid = false;
    } else if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const data = await login(email, password);

      if (process.env.NODE_ENV === "development") {
        console.log("Login: API call successful, token stored in localStorage");
      }

      // Show success state
      setSuccess(true);

      // Apply token immediately to context to prevent blank interim state
      applyToken(data.token);
      // Small delay to let UI animate
      await new Promise((resolve) => setTimeout(resolve, 50));
      // Ensure any other listeners sync too
      refreshAuthState();
      navigate("/admin-dashboard", { replace: true });
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || "";

      // Deliberately generic for bad credentials: the backend avoids
      // revealing whether an email exists, and so do we.
      if (
        errorMessage.toLowerCase().includes("rate limit") ||
        errorMessage.toLowerCase().includes("too many")
      ) {
        setError(
          "Too many login attempts. Please wait a moment before trying again."
        );
      } else if (errorMessage.toLowerCase().includes("invalid")) {
        setError(
          "Invalid email or password. Please check your credentials and try again."
        );
      } else {
        setError("Login failed. Please check your connection and try again.");
      }

      setSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
      id="login-container"
      className="flex flex-col justify-center items-center p-6 lg:p-10 min-h-screen bg-white dark:bg-gradient-to-br dark:from-[#1a1a1a] dark:to-brand-dark"
    >
      {/* Back to Home Link */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
        className="self-start mb-6"
      >
        <Link
          to="/"
          className="flex items-center text-gray-800 dark:text-brand-cream hover:text-brand-gold transition-colors duration-300 group"
          aria-label="Go back to home page"
        >
          <FiArrowLeft className="mr-2 transform group-hover:-translate-x-1 transition-transform duration-300" />
          Back to Portfolio
        </Link>
      </motion.div>

      {/* Login Card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2, duration: 0.3 }}
        className="w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl lg:text-5xl font-bold mb-3"
          >
            <span className="text-gradient">
              Admin Login
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-gray-600 dark:text-brand-cream/70 text-lg"
          >
            Access your dashboard
          </motion.p>
        </div>

        {/* Login Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          onSubmit={handleSubmit}
          className="bg-white/90 dark:bg-[#2a2a2a]/80 backdrop-blur-sm border border-gray-200 dark:border-brand-purple/20 shadow-2xl rounded-2xl p-8 space-y-6"
          noValidate
        >
          {/* Success Message */}
          <AnimatePresence>
            {success && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                className="flex items-center space-x-3 p-4 bg-green-500/20 border border-green-500/30 rounded-lg text-green-400"
              >
                <FiCheckCircle className="text-xl flex-shrink-0" />
                <span>Login successful! Opening dashboard...</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error Message */}
          <AnimatePresence>
            {error && !success && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                className="flex items-center space-x-3 p-4 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400"
                role="alert"
                aria-live="polite"
              >
                <FiAlertCircle className="text-xl flex-shrink-0" />
                <span>{error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Email Field */}
          <div className="space-y-2">
            <label
              htmlFor="email"
              className="block text-gray-800 dark:text-brand-cream text-sm font-semibold"
            >
              Email Address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiMail
                  className={`text-lg transition-colors duration-300 ${
                    focusedField === "email"
                      ? "text-brand-gold"
                      : "text-gray-400 dark:text-brand-cream/40"
                  }`}
                />
              </div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError("");
                  if (error) setError("");
                }}
                onFocus={() => setFocusedField("email")}
                onBlur={() => setFocusedField("")}
                className={`w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-[#1a1a1a] border-2 rounded-xl text-gray-800 dark:text-brand-cream placeholder-gray-400 dark:placeholder-brand-cream/50 transition-all duration-300 focus:outline-none ${
                  emailError
                    ? "border-red-500 focus:border-red-400"
                    : focusedField === "email"
                    ? "border-brand-gold focus:border-brand-gold"
                    : "border-brand-purple/30 focus:border-brand-purple hover:border-brand-purple/50"
                }`}
                placeholder="Enter your email"
                disabled={isLoading || success}
                autoComplete="email"
                aria-describedby={emailError ? "email-error" : undefined}
                aria-invalid={emailError ? "true" : "false"}
              />
            </div>
            <AnimatePresence>
              {emailError && (
                <motion.p
                  id="email-error"
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  className="text-red-400 text-sm"
                  role="alert"
                >
                  {emailError}
                </motion.p>
              )}
            </AnimatePresence>
          </div>

          {/* Password Field */}
          <div className="space-y-2">
            <label
              htmlFor="password"
              className="block text-gray-800 dark:text-brand-cream text-sm font-semibold"
            >
              Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiLock
                  className={`text-lg transition-colors duration-300 ${
                    focusedField === "password"
                      ? "text-brand-gold"
                      : "text-gray-400 dark:text-brand-cream/40"
                  }`}
                />
              </div>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (passwordError) setPasswordError("");
                  if (error) setError("");
                }}
                onFocus={() => setFocusedField("password")}
                onBlur={() => setFocusedField("")}
                className={`w-full pl-10 pr-12 py-3 bg-gray-50 dark:bg-[#1a1a1a] border-2 rounded-xl text-gray-800 dark:text-brand-cream placeholder-gray-400 dark:placeholder-brand-cream/50 transition-all duration-300 focus:outline-none ${
                  passwordError
                    ? "border-red-500 focus:border-red-400"
                    : focusedField === "password"
                    ? "border-brand-gold focus:border-brand-gold"
                    : "border-brand-purple/30 focus:border-brand-purple hover:border-brand-purple/50"
                }`}
                placeholder="Enter your password"
                disabled={isLoading || success}
                autoComplete="current-password"
                aria-describedby={passwordError ? "password-error" : undefined}
                aria-invalid={passwordError ? "true" : "false"}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-600 dark:text-brand-cream/60 hover:text-brand-gold transition-colors duration-300"
                disabled={isLoading || success}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <FiEyeOff className="text-lg" />
                ) : (
                  <FiEye className="text-lg" />
                )}
              </button>
            </div>
            <AnimatePresence>
              {passwordError && (
                <motion.p
                  id="password-error"
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  className="text-red-400 text-sm"
                  role="alert"
                >
                  {passwordError}
                </motion.p>
              )}
            </AnimatePresence>
          </div>

          {/* Submit Button */}
          <motion.button
            whileHover={{ scale: isLoading || success ? 1 : 1.02 }}
            whileTap={{ scale: isLoading || success ? 1 : 0.98 }}
            type="submit"
            disabled={isLoading || success}
            className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-brand-gold/30 ${
              isLoading || success
                ? "bg-gray-600 cursor-not-allowed text-white"
                : "bg-brand-gold hover:bg-brand-gold/90 text-black shadow-lg hover:shadow-glow"
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center space-x-2">
                <FiLoader className="animate-spin text-xl" />
                <span>Signing In...</span>
              </span>
            ) : success ? (
              <span className="flex items-center justify-center space-x-2">
                <FiCheckCircle className="text-xl" />
                <span>Success!</span>
              </span>
            ) : (
              "Sign In to Dashboard"
            )}
          </motion.button>

          {/* Helper Text */}
          <div className="text-center">
            <p className="text-gray-600 dark:text-brand-cream/60 text-sm">
              Having trouble? Check your credentials and try again.
            </p>
          </div>
        </motion.form>

        {/* Footer Note */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="text-center mt-6"
        >
          <p className="text-gray-600 dark:text-brand-cream/50 text-sm">
            This is a private admin area for portfolio management.
          </p>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

export default LoginPage;
